/**
 * @file mobileForm.tsx
 * @brief The phone's Edit Profile form pieces, for the web Profile trial.
 *
 * @details
 * DES (01-10-2026): "sirf is page per kia hum mobile jaisa form style try kar
 * saktay hain, if we like it good otherwise change kar dain gay". Two ports,
 * used by `ProfilePage` only while `PROFILE_MOBILE_FORM` is on:
 *
 * - `FolderCard`: mobile `VZFieldset` soft + `SOFT_LEGEND = 'folder'`. A
 *   rounded card (radius 32) with its heading on a tab BEHIND it, rising 36px
 *   above the top-left, right side a 45° slant with 8px soft corners. Light:
 *   the card fill under a 7% black wash; dark: bgSoft, --muted (the tab
 *   follows the page, not the card). Heading bold 15 at 0.5 opacity, the
 *   `VZFormSection` label; tab text 22px in, card padding 16.
 * - `BareField`: mobile `VZInput labelVariant="bare"` with `bareShowsIcon`.
 *   No fill or border: a 20px brand icon, the LABEL as placeholder, which
 *   shrinks to a 13px caption once there is a value; value medium 17; a
 *   hairline under each field from the text column, none under the last.
 *
 * Nothing else imports these; delete with the trial if DES says no.
 *
 * @author DES
 * @date 01-10-2026
 */

import type { InputHTMLAttributes, ReactNode } from "react";
import { cn } from "@/shared/theme/cn";

const CARD_R = 32;
const TAB_H = 36;
const TAB_SLANT = TAB_H;
const TAB_ROUND = 8;
const TAB_SVG_W = TAB_ROUND + TAB_SLANT + TAB_ROUND;
const TAB_PATH = (() => {
  const c = TAB_ROUND;
  const d = c * Math.SQRT1_2;
  const r2 = (n: number) => Math.round(n * 100) / 100;
  const xTop = c;
  const xBot = c + TAB_SLANT;
  return [
    "M0 0",
    `H${r2(xTop - c)}`,
    `Q${xTop} 0 ${r2(xTop + d)} ${r2(d)}`,
    `L${r2(xBot - d)} ${r2(TAB_H - d)}`,
    `Q${xBot} ${TAB_H} ${r2(xBot + c)} ${TAB_H}`,
    `V${TAB_H + CARD_R}`,
    "H0 Z",
  ].join(" ");
})();

/** Tab colour: white + 7% black in light, bgSoft (--muted) in dark. */
const TAB_TONE = "bg-[#EDEDED] text-[#EDEDED] dark:bg-muted dark:text-muted";

export function FolderCard({
  label,
  children,
  className,
}: {
  label: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={cn("relative", className)} style={{ paddingTop: TAB_H }}>
      {/* Drawn first so the card paints over its lower part. */}
      <div
        className="pointer-events-none absolute left-0 top-0 flex"
        style={{ height: TAB_H + CARD_R }}
        aria-hidden
      >
        <div
          className={cn("overflow-hidden pl-[22px]", TAB_TONE)}
          style={{ borderTopLeftRadius: CARD_R, paddingRight: 28 - TAB_ROUND }}
        >
          <div className="flex items-center" style={{ height: TAB_H }}>
            <span className="text-[15px] font-bold tracking-[-0.2px] text-app-text opacity-50">
              {label}
            </span>
          </div>
        </div>
        <svg
          width={TAB_SVG_W}
          height={TAB_H + CARD_R}
          className={cn("shrink-0", TAB_TONE, "bg-transparent dark:bg-transparent")}
        >
          <path d={TAB_PATH} fill="currentColor" />
        </svg>
      </div>
      <h3 className="sr-only">{label}</h3>
      <div className="relative min-h-16 bg-app-card p-4" style={{ borderRadius: CARD_R }}>
        {children}
      </div>
    </section>
  );
}

export function BareField({
  label,
  icon,
  trailing,
  last = false,
  className,
  value,
  ...input
}: InputHTMLAttributes<HTMLInputElement> & {
  label: string;
  icon: ReactNode;
  /** e.g. the password eye, at the right of the value line. */
  trailing?: ReactNode;
  /** The last field in its card: no hairline under it. */
  last?: boolean;
  value: string;
}) {
  const filled = value.length > 0;
  return (
    <label className={cn("relative block pb-2.5 pt-2.5", className)}>
      {/* Caption over the value, in the text column (hidden while empty:
          the label is then the placeholder). */}
      <span
        className={cn(
          "mb-[3px] block pl-8 text-[13px] font-medium leading-4 text-muted-foreground opacity-[0.65]",
          !filled && "invisible",
        )}
      >
        {label}
      </span>
      <span className="flex min-h-6 items-center gap-3">
        <span className="flex w-5 shrink-0 items-center justify-center text-brand">{icon}</span>
        <input
          {...input}
          value={value}
          placeholder={label}
          aria-label={label}
          className="w-full min-w-0 flex-1 bg-transparent text-[17px] font-medium leading-[21px] text-app-text outline-none placeholder:text-muted-foreground/70 disabled:cursor-not-allowed disabled:opacity-60"
        />
        {trailing}
      </span>
      {last ? null : (
        <span className="absolute bottom-0 left-8 right-0 h-px bg-app-text/10" aria-hidden />
      )}
    </label>
  );
}
